import React, { useState, useEffect } from 'react';
import { X, Users, Check, Search } from 'lucide-react';
import { contactService } from '../../services/contactService';
import { contactGroupService } from '../../services/contactGroupService';
import toast from 'react-hot-toast';

const AddContactsToGroupModal = ({ isOpen, onClose, group, onSuccess }) => {
  const [allContacts, setAllContacts] = useState([]);
  const [groupContacts, setGroupContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen && group) {
      loadData();
    }
  }, [isOpen, group]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all contacts with pagination
      const contactsResponse = await contactService.getContacts(0, 1000);
      const allContactsList = contactsResponse.content || [];
      setAllContacts(allContactsList);

      // Load contacts already in the group
      if (group.groupId) {
        const groupDetails = await contactService.getContactGroupDetails(group.groupId);
        const existingContacts = groupDetails.members || [];
        setGroupContacts(existingContacts);
        
        // Filter out contacts already in the group
        const existingContactIds = new Set(existingContacts.map(c => c.contactId));
        const availableContacts = allContactsList.filter(
          c => !existingContactIds.has(c.contactId)
        );
        setAllContacts(availableContacts);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleContact = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const handleSave = async () => {
    if (!group || selectedContacts.length === 0) {
      toast.info('Please select at least one contact');
      return;
    }

    setSaving(true);
    try {
      await contactGroupService.addContactsToGroup(group.groupId, selectedContacts);
      toast.success(`Added ${selectedContacts.length} contact(s) to group`);
      onSuccess && onSuccess();
      onClose();
      // Reset state
      setSelectedContacts([]);
      setSearchTerm('');
    } catch (error) {
      console.error('Error adding contacts to group:', error);
      toast.error(error.response?.data?.message || 'Failed to add contacts to group');
    } finally {
      setSaving(false);
    }
  };

  const filteredContacts = allContacts.filter(contact => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      (contact.contactName || '').toLowerCase().includes(searchLower) ||
      (contact.phoneNumber || '').includes(searchTerm)
    );
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          onClick={onClose}
        />
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-6 h-6 text-primary-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Add Contacts to Group
                </h3>
              </div>
              <button
                onClick={onClose}
                disabled={saving}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {group && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900">{group.groupName}</p>
                <p className="text-xs text-gray-500">
                  {group.totalContacts || 0} contact{group.totalContacts !== 1 ? 's' : ''} currently in group
                </p>
              </div>
            )}

            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {filteredContacts.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    {searchTerm ? 'No contacts found matching your search' : 'No contacts available to add'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {filteredContacts.map((contact) => {
                      const isSelected = selectedContacts.includes(contact.contactId);

                      return (
                        <div
                          key={contact.contactId}
                          onClick={() => handleToggleContact(contact.contactId)}
                          className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{contact.contactName || 'Unnamed'}</p>
                            <p className="text-xs text-gray-500">{contact.phoneNumber}</p>
                          </div>
                          <div className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center ${
                            isSelected
                              ? 'border-primary-600 bg-primary-600'
                              : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {selectedContacts.length > 0 && (
              <div className="mt-4 p-3 bg-primary-50 rounded-lg">
                <p className="text-sm text-primary-700">
                  {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
                </p>
              </div>
            )}
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={handleSave}
              disabled={saving || loading || selectedContacts.length === 0}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium bg-primary-600 text-white hover:bg-primary-700 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Adding...' : `Add ${selectedContacts.length > 0 ? `(${selectedContacts.length})` : ''}`}
            </button>
            <button
              onClick={onClose}
              disabled={saving}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddContactsToGroupModal;

